import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import { SEASONS_PROP_TYPES } from '../../lib/prop-types/season';
import ClubRouter from '../../lib/routing';
import withTeam from '../../lib/with-team';
import NoBackground from '../../components/club-frame/header/no-background';
import SeoHead from '../../components/seo';
import TeamLeagueTable from '../../components/team/league_table';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import { fetchLeagueTablePageData } from '../../store/teams/league-table/actions';
import { selectSeasonAction } from '../../store/clubs/actions';
import { getCurrentClubSeasons, getActiveSeasonId } from '../../store/clubs/selectors';
import {
  isLoading,
  hasLoaded,
  getLoadingError,
  getTablesBySeason,
  getDefaultTable,
  getTable,
} from '../../store/teams/league-table/selectors';
import TABLE_PROP_TYPES from '../../lib/prop-types/table';

class TeamLeagueTablePage extends React.Component {
  static async getInitialProps({ reduxStore, club, isServer, team, req, res, query }) {
    const initialProps = {
      activeSubSection: 'league_table',
      pageType: 'team.league_table',
      seasonId: query.season
        ? parseInt(query.season, 10)
        : getActiveSeasonId(reduxStore.getState()),
      tableId: parseInt(query.table, 10),
    };

    if (!isServer) {
      return initialProps;
    }

    const { error, accessLevel } = await reduxStore.dispatch(
      fetchLeagueTablePageData(club.id, team.id, req),
    );

    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }

    if (accessLevel && accessLevel === 'private') {
      res.doNotCache = true;
    }

    return initialProps;
  }

  componentDidMount() {
    const {
      loading,
      previouslyLoaded,
      loadPageData,
      club: { id: clubId },
      team: { id: teamId },
    } = this.props;

    if (!loading && !previouslyLoaded) {
      loadPageData(clubId, teamId);
    }
  }

  onSeasonFilterChangeHandler = (event) => {
    const seasonId = parseInt(event.target.value, 10);
    const {
      club,
      team: { id: teamId },
      selectSeason,
    } = this.props;
    selectSeason(seasonId);
    ClubRouter.pushClubRoute(club, 'team.league_table', { teamId, season: seasonId });
  };

  onTableFilterChangeHandler = (event) => {
    const tableId = event.target.value;
    const {
      club,
      team: { id: teamId },
      seasonId,
    } = this.props;

    const params = {
      teamId,
      table: tableId,
    };

    if (seasonId) {
      params.season = seasonId;
    }
    ClubRouter.pushClubRoute(club, 'team.league_table', params);
  };

  render() {
    const { club, team, loadingError, loading, seasons, seasonId, tables, table, tableId } =
      this.props;
    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    return (
      <>
        <SeoHead
          club={club}
          descriptionTranslationKey="teams:league_tables_meta_description"
          route="team.fixtures"
          routeParams={{ teamId: team.id }}
          title={table && table.name}
          titleTranslationKey="teams:league_tables_page_title"
          translationValues={{ teamName: team.name }}
        />
        <NoBackground />
        <TeamLeagueTable
          club={club}
          isLoading={loading}
          seasons={seasons}
          seasonId={seasonId}
          tables={tables}
          table={table}
          tableId={tableId}
          onSeasonFilterChange={this.onSeasonFilterChangeHandler}
          onTableFilterChange={this.onTableFilterChangeHandler}
        />
      </>
    );
  }
}

TeamLeagueTablePage.defaultProps = {
  loadingError: null,
  table: null,
  seasonId: null,
  tableId: null,
};

TeamLeagueTablePage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  team: TEAM_PROP_TYPES.isRequired,
  loading: PropTypes.bool.isRequired,
  previouslyLoaded: PropTypes.bool.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  loadPageData: PropTypes.func.isRequired,
  seasons: SEASONS_PROP_TYPES.isRequired,
  tables: PropTypes.arrayOf(TABLE_PROP_TYPES).isRequired,
  table: TABLE_PROP_TYPES,
  seasonId: PropTypes.number,
  selectSeason: PropTypes.func.isRequired,
  tableId: PropTypes.number,
};

const dispatchToProps = {
  loadPageData: fetchLeagueTablePageData,
  selectSeason: selectSeasonAction,
};

const mapStateToProps = (state, { seasonId, tableId, teamId }) => ({
  loading: isLoading(state, teamId),
  previouslyLoaded: hasLoaded(state, teamId),
  loadingError: getLoadingError(state, teamId),
  seasons: getCurrentClubSeasons(state),
  tables: getTablesBySeason(state, teamId, seasonId),
  table: tableId ? getTable(state, teamId, tableId) : getDefaultTable(state, teamId, seasonId),
});

export default withTeam(connect(mapStateToProps, dispatchToProps)(TeamLeagueTablePage));
